import { z } from 'zod';
import { AuditSchema, IdSchema, InstantSchema } from './common.ts';
import { RecordRefSchema } from './notification.ts';

/**
 * A section of a job's specification sheet (SCRUM-141, P8-05).
 *
 * `docs/features/13-later-modules.md`: "Per-job spec sheets (rooms/sections,
 * items, finishes, model numbers, files), owner-visible, printable."
 *
 * **One document per section, items inside it.** The three candidate
 * shapes and why this one:
 *
 * - *One document for the whole sheet* reads in a single get and reorders
 *   trivially, and two people editing different rooms would collide on
 *   every save. A spec sheet is written by whoever is standing in the room.
 * - *One document per item* scales without limit and makes the printable
 *   sheet — the thing this module exists for — a query that has to be
 *   grouped and ordered client-side, with an index per ordering.
 * - *One document per section* is what the repository already does with
 *   anything always read together and short: a to-do's checklist, a
 *   warranty claim's visits. Items are capped so the document cannot grow
 *   without bound.
 *
 * **`shared` is per section, not per sheet.** A builder specifies the
 * kitchen in week one and the basement in month four, and a single
 * sheet-level switch would mean either showing the owner an empty basement
 * or withholding the kitchen until the whole house is written up.
 */
export const MAX_SPEC_ITEMS = 100;

export const SpecItemSchema = z.object({
  id: IdSchema,
  name: z.string().trim().min(1, 'An item needs a name').max(200),
  /** Manufacturer. Named `brand` because that is the word on a spec sheet. */
  brand: z.string().trim().max(120).default(''),
  model: z.string().trim().max(120).default(''),
  finish: z.string().trim().max(200).default(''),
  /**
   * Free text, deliberately — "2 sets", "per plan", "120 sq ft".
   *
   * Everywhere money or a billable quantity appears in this product it is a
   * scaled integer, because arithmetic is done on it. Nothing adds up a
   * spec sheet: it is a description of what was agreed, and a number field
   * here would force "per plan" to become a lie or a blank.
   */
  quantity: z.string().trim().max(60).default(''),
  notes: z.string().trim().max(2_000).default(''),
  supplierContactId: IdSchema.optional(),
});
export type SpecItem = z.infer<typeof SpecItemSchema>;

export const SpecSectionSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1, 'A section needs a title').max(200),
  /** Sort key within the sheet. Sparse on purpose — see `nextPosition`. */
  position: z.number().int().min(0),
  shared: z.boolean().default(false),
  /**
   * When it was first shown to the owner, and never cleared.
   *
   * It is what makes "delete" and "unshare" different acts. Unsharing takes
   * a section off the owner's sheet; deleting it destroys what they were
   * shown, and a section they have read is part of the record of what was
   * agreed. So a section that has ever been shared cannot be deleted.
   */
  firstSharedAt: InstantSchema.optional(),
  notes: z.string().trim().max(20_000).default(''),
  items: z.array(SpecItemSchema).max(MAX_SPEC_ITEMS).default([]),
  linkedRecords: z.array(RecordRefSchema).default([]),
  linkedKeys: z.array(z.string().min(1)).default([]),
  version: z.number().int().positive(),
});
export type SpecSection = z.infer<typeof SpecSectionSchema>;

/**
 * Positions are spaced, not consecutive.
 *
 * A new section lands at the end with a gap above it, so inserting between
 * two existing sections later is one write to one document rather than a
 * renumbering of everything below. Consecutive integers would make every
 * insert a transaction over the whole sheet, which is how a reorder comes
 * to half-apply.
 */
export const POSITION_STEP = 1000;

export function nextPosition(sections: readonly { readonly position: number }[]): number {
  // Starting an empty sheet at `POSITION_STEP` rather than at 0 is not
  // cosmetic: `position` is a non-negative integer, so a first section at
  // 0 leaves **nowhere above it**, and the first thing anybody does to a
  // sheet of one section is add a second and decide it goes first. The
  // use-case tests caught it by doing exactly that and meeting "there is
  // no room between those two sections".
  return sections.reduce((highest, one) => Math.max(highest, one.position), 0) + POSITION_STEP;
}

/**
 * The position that sorts between two sections.
 *
 * Returns `null` when there is no room left — two adjacent positions with
 * nothing between them. The caller renumbers rather than rounding, because
 * rounding silently puts the section somewhere it was not dropped.
 */
export function positionBetween(above: number | null, below: number | null): number | null {
  if (above === null && below === null) return 0;
  // Above everything. `position` is a non-negative integer — the schema
  // says so — so a sheet whose first section already sits at 0 has no room
  // above it and has to be renumbered. Returning -1000 here would produce a
  // document that fails validation on save, which is a worse way to find
  // out than being told there is no room.
  if (above === null) return below! <= 0 ? null : Math.max(0, below! - POSITION_STEP);
  if (below === null) return above + POSITION_STEP;
  if (below - above < 2) return null;
  return Math.floor((above + below) / 2);
}

/** The sheet in the order it prints. */
export function inSheetOrder(sections: readonly SpecSection[]): SpecSection[] {
  // `position` then `id`, because two sections can share a position: the
  // gap-filling above makes it unlikely rather than impossible, and a sheet
  // that reorders itself between two reads is worse than one with two
  // sections in an arbitrary but stable order.
  return [...sections].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
}

/** Whether anything on this section is worth showing somebody. */
export function isSpecSectionEmpty(section: Pick<SpecSection, 'items' | 'notes'>): boolean {
  return section.items.length === 0 && section.notes.trim().length === 0;
}
